import React, { useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import actions from "store/actions";
import { State } from "store/reducers";
import Report from "Components/Report";
import ls from "Localization";
import ISplit from "Types/Split";
import { toStringCurrency } from "helpers/string";
import ReactTable from "react-table";
import _ from "lodash";
import INFSe from "Types/INFSe";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { NFSeStatus } from "Types/INFSe";
import { CNPJ } from "Components/NumberFormat";
import { NFSeState } from "store/reducers/nfse";
import { dateSort } from "Components/Report/helpers";
import DateSelectFilter from "Components/Report/DateSelectorFilter";
import Tooltip from "Components/Tooltip";
import { FaFileCode, FaRegFilePdf } from "react-icons/fa";

dayjs.extend(utc);

let getNfsesDebounced: (() => any) & _.Cancelable;

function NFSe({ id }) {
	const dispatch = useDispatch<any>();
	const table = useRef<ReactTable<any>>(null);

	const state = useSelector<State, NFSeState>(s => s.nfse);

	const handleFetchData = (tableState: any) => {
		if (!tableState) return;
		const { page, pageSize, toExport, callback } = tableState;
		let { sorted, filtered } = tableState;

		if (getNfsesDebounced) {
			getNfsesDebounced.cancel();
		}

		if (!sorted) sorted = [];

		filtered = [...(filtered || [])];

		const creationDate = filtered.find((c: any) => c.id === "creationDate");

		if (creationDate?.value) {
			filtered = filtered.filter((c: any) => c.id !== "creationDate");

			if (creationDate?.value.startDate) {
				filtered.push({
					id: "startDate",
					value: creationDate.value.startDate
				});
			}

			if (creationDate?.value.endDate)
				filtered.push({
					id: "endDate",
					value: creationDate.value.endDate
				});
		}

		filtered.push({
			id: "companyId",
			value: id
		});

		getNfsesDebounced = _.debounce(
			() =>
				dispatch(
					actions.nfse.getNFSes(
						page * pageSize,
						pageSize,
						filtered.reduce(
							(p: any, c: any) => p + `&filters[${c.id}]=${c.value}`,
							""
						),
						sorted[0]?.id,
						sorted[0]?.desc,
						toExport,
						callback
					)
				),
			500
		);

		getNfsesDebounced();
	};

	const columns = useMemo(() => {
		return [
			{
				Header: ls.creationDate,
				id: "createdAt",
				accessor: (c: INFSe) =>
					dayjs(c.createdAt).format(ls.dateFormatWithoutSeconds),
				show: true,
				sortMethod: dateSort,
				width: 150,
				Filter: p => <DateSelectFilter {...p} />
			},
			{
				Header: "NFS-e",
				id: "code",
				show: true,
				accessor: (c: INFSe) => c.code
			},
			{
				Header: "Prestador",
				id: "mei",
				show: true,
				accessor: (c: INFSe) =>
					c.mei?.personName || c.mei?.businessName || c.mei?.cnpj
			},
			{
				Header: "CNPJ Prestador",
				id: "document",
				show: true,
				accessor: (c: INFSe) => <CNPJ value={c.mei?.cnpj} displayType="text" />,
				width: 180
			},
			{
				Header: ls.value,
				id: "value",
				show: true,
				accessor: (c: INFSe) => toStringCurrency(+c.value / 100.0),
				width: 130
			},
			{
				Header: ls.pdf,
				id: "pdf",
				show: false,
				accessor: (c: INFSe) => c.pdf,
				width: 150
			},

			{
				Header: ls.xml,
				id: "xml",
				show: false,
				accessor: (c: INFSe) => c.xml,
				width: 150
			},
			{
				Header: ls.status,
				id: "status",
				show: true,
				accessor: (c: INFSe) => {
					if (c.status === NFSeStatus.Error) {
						return (
							<Tooltip title={c.statusReason}>
								{ls[NFSeStatus[c.status]]}
							</Tooltip>
						);
					}

					return ls[NFSeStatus[c.status]];
				},
				width: 120,
				Filter: ({ filter, onChange }: any) => {
					return (
						<select
							onChange={event => onChange(event.target.value)}
							style={{ width: "100%" }}
							value={filter && filter.value ? filter.value : "all"}
						>
							<option value="">Todos</option>
							{Object.keys(NFSeStatus)
								.filter(c => !isNaN(+c))
								.map((c: any) => (
									<option key={c} value={c}>
										{ls[NFSeStatus[c]]}
									</option>
								))}
						</select>
					);
				}
			},
			{
				Header: "Criado por",
				id: "createdBy",
				show: true,
				accessor: (c: INFSe) => {
					if (c.createdBy) {
						return `${c.createdBy.name || ""} ${c.createdBy.lastname || ""}`.trim() || "N/A";
					}
					return "N/A";
				},
				width: 200
			},
			{
				Header: ls.actions,
				id: "actions",
				accessor: (c: any) => (
					<>
						{c.pdf && (
							<div
								style={{
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									flex: 1
								}}
							>
								<a href={c.pdf} target="_blank">
									<FaRegFilePdf />
								</a>
							</div>
						)}
						{c.xml && (
							<div
								style={{
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									flex: 1
								}}
							>
								<a href={c.xml} target="_blank">
									<FaFileCode />
								</a>
							</div>
						)}
					</>
				),
				show: true,
				filterable: false,
				width: 60
			}
		];
	}, []);

	const load = () => handleFetchData(table.current?.state);

	return (
		<Report
			manual
			tableRef={table}
			title="Notas fiscais"
			onFetchData={handleFetchData}
			data={state.items}
			pages={state.pages}
			loading={state.loading.getAll}
			columns={columns}
			defaultSorted={[
				{
					id: "date",
					desc: true
				}
			]}
			filterable
			visibleColumns={columns.filter(c => c.show).map(c => c.Header.toString())}
			getTdProps={() => {
				return {
					style: {
						display: "flex",
						alignItems: "center"
					}
				};
			}}
		/>
	);
}

export default NFSe;
